import Form from "@rjsf/react-bootstrap";
import React, { FunctionComponent, useEffect, useState } from "react";
import { JSONSchema7 } from "json-schema";
import { Link, useNavigate } from "react-router";
import { toast } from "react-toastify";
import firebase from "firebase/compat/app";
import { Alert, Spinner, Container, Card, Row, Col } from "react-bootstrap";
import { UiSchema } from "@rjsf/utils";
import validator from "@rjsf/validator-ajv8";
import { Helmet } from "react-helmet";

import { PrimaryButton } from "../common/button/Buttons";
import { getErrorByCode } from "../../helper/firebase/getErrorByCode";
import { useAuthResetPassword } from "../../hooks/useAuthResetPassword";
import WebtieLogo from "../common/webtie-logo.png";

const schema: JSONSchema7 = {
  type: "object",
  required: ["email"],
  properties: {
    email: {
      type: "string",
      title: "E-Mail"
    }
  }
};

const uiSchema: UiSchema = {
  email: {
    "ui:widget": "email"
  }
};

interface Props {
  user: firebase.User | null | undefined;
}

const ResetPassword: FunctionComponent<Props> = ({ user }: Props) => {
  const navigate = useNavigate();
  const { resetPassword, isLoading } = useAuthResetPassword();
  const [resetSubmitted, setResetSubmitted] = useState<boolean>(false);

  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  const submitResetPassword = async (formData: any): Promise<void> => {
    const { email } = formData;

    resetPassword(email)
      .then(() => setResetSubmitted(true))
      .catch((e) => {
        toast.error(`Zurücksetzen des Passworts ist fehlgeschlagen. ${getErrorByCode(e.code)}`);
      });
  };

  return (
    <>
      <Helmet>
        <title>Spielgruppe Online - Passwort zurücksetzen</title>
        <meta
          name="description"
          content="Setzen Sie Ihr Passwort zurück, um wieder Zugang zu Ihrem Konto zu erhalten"
        />
      </Helmet>
      <div
        className="min-vh-100 d-flex flex-column"
        style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
        <Container className="flex-grow-1 d-flex align-items-center justify-content-center py-5">
          <Row className="w-100 justify-content-center">
            <Col md={6} lg={5} xl={4}>
              <Card className="shadow-lg border-0 rounded-lg">
                <Card.Body className="p-5">
                  <div className="text-center mb-4">
                    <img
                      src={WebtieLogo}
                      alt="Webtie Logo"
                      className="img-fluid mb-3"
                      style={{ maxWidth: "200px", height: "auto" }}
                    />
                    <h2 className="text-dark mb-3" style={{ fontFamily: "Varela, sans-serif", fontWeight: "600" }}>
                      Passwort zurücksetzen
                    </h2>
                    <p className="text-muted mb-4">Geben Sie Ihre E-Mail ein, um Ihr Passwort zurückzusetzen</p>
                  </div>
                  <Form
                    validator={validator}
                    schema={schema}
                    uiSchema={uiSchema}
                    onSubmit={(e) => submitResetPassword(e.formData)}
                    showErrorList={false}
                    className="reset-form">
                    <div className="mb-3">
                      <p className="small text-muted text-center">
                        Wenn Sie das Passwort zurücksetzen, erhalten Sie eine E-Mail mit den Anweisungen zum
                        Zurücksetzen des Passworts.
                      </p>
                    </div>
                    <div className="d-grid gap-2">
                      <PrimaryButton active={!isLoading && !resetSubmitted} type="submit" size="lg">
                        Passwort zurücksetzen {isLoading && <Spinner size={"sm"} animation={"border"} />}
                      </PrimaryButton>
                    </div>
                  </Form>

                  {resetSubmitted && (
                    <Alert variant={"success"} className={"mt-3"}>
                      Bitte prüfen Sie Ihr E-Mail Postfach und folgen Sie den Anweisungen des erhaltenen E-Mails.
                    </Alert>
                  )}

                  <div className="text-center mt-4">
                    <p className="text-muted">
                      Wissen Sie Ihr Passwort noch?{" "}
                      <Link to="/" className="text-decoration-none fw-bold">
                        Hier gehts zum Login
                      </Link>
                    </p>
                  </div>
                </Card.Body>
              </Card>

              <div className="text-center mt-4">
                <p className="text-white-50 small">Spielgruppe Online - Anmeldungen einfach verwalten</p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default ResetPassword;
